import React, { useEffect, useMemo, useState, useContext } from 'react'
import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  ApprovalStatus,
  useGetFaCategoryDropdownListingQuery,
  useGetAssetRegistrationQuery,
  useGetCurrentCategoryInfoLazyQuery,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useCreateFaCategoryChangeMutation,
  useUpdateFaCategoryChangeMutation,
} from 'generated/graphql'
import * as yup from 'yup'

interface CurrentCategoryInfo {
  currentCategoryId: string
  currentCategoryName: string
  originalCategoryId: string
  originalCategoryName: string
  currentOriginalValue: number
  currentAccumulatedDepreciation: number
  currentNetBookValue: number
  quantity: number
  currentUsefulLife: number
}

// ============================================================================
// Component
// ============================================================================

export const ChangeAssetCategoryForm = (props: any) => {
  let history = useHistory()
  let location = useLocation()

  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(null)
  const [record, setRecord] = useState(false)
  const [, setIsFormDirty] = useState(true)
  const [selectedAsset, setSelectedAsset] = useState<any>(
    editData?.Asset || null
  )
  const [selectedCategory, setSelectedCategory] = useState<any>(
    editData?.NewCategory || null
  )
  const [currentInfo, setCurrentInfo] = useState<CurrentCategoryInfo | null>(
    null
  )
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let form, mode
  switch (formMode) {
    case 'add':
      form = 'New'
      mode = 'add'
      break
    case 'edit':
      form = 'Draft'
      mode = 'edit'
      break
    case 'approve-reject':
      form = 'Approve'
      mode = 'approve-reject'
      break
    case 'resubmit':
      form = 'Resubmit'
      mode = 'edit'
      break
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const changeAssetCategorySchema = yup.object().shape({
    fa_asset_id: yup.string().required('Asset is required'),
    new_category_id: yup.string().required('New Asset Category is required'),
    change_date: yup.date().required('Change Date is required'),
    description: yup.string().nullable(),
    reason: yup.string().required('Reason is required'),
  })

  const { register, setValue, control, handleSubmit, errors, watch } = useForm<
    any
  >({
    defaultValues: {
      fa_asset_id: editData?.fa_asset_id || '',
      new_category_id: editData?.new_category_id || '',
      change_date: editData?.change_date || new Date(),
      description: editData?.description || '',
      reason: editData?.reason || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(changeAssetCategorySchema),
  })

  /* -------------------------------------------- */
  /*                    QUERY                    */
  /* -------------------------------------------- */
  const {
    loading: FACategoryListingLoading,
    data: { getFACategoryDropdownListing } = {
      getFACategoryDropdownListing: [],
    },
  } = useGetFaCategoryDropdownListingQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: FAAssetLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    getCurrentCategoryInfo,
    { loading: currentInfoLoading },
  ] = useGetCurrentCategoryInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentCategoryInfo) {
        const info = data.getCurrentCategoryInfo as CurrentCategoryInfo
        setCurrentInfo(info)
        setValue('AssetCategory', info.currentCategoryName || 'N/A')
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const getFAAssetDropdown = useMemo(() => {
    return getAssetRegistration || []
  }, [getAssetRegistration])

  const [
    createCategoryChange,
    { loading: createLoading },
  ] = useCreateFaCategoryChangeMutation({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/change-asset-category`
      )
    },
  })

  const [
    updateCategoryChange,
    { loading: updateLoading },
  ] = useUpdateFaCategoryChangeMutation({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/change-asset-category`
      )
    },
  })

  /* -------------------------------------------- */
  /*                   EFFECTS                    */
  /* -------------------------------------------- */
  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      getFAAssetDropdown.length > 0
    ) {
      const asset = getFAAssetDropdown.find(
        a => a.fa_asset_id === editData.fa_asset_id
      )
      if (asset) {
        setSelectedAsset(asset)
        setValue('AssetName', asset.name || '')
        setValue('StartDate', asset.start_use_date)
        setValue('ModelNo', asset.model_no || '')

        getCurrentCategoryInfo({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_change_id: editData.category_change_id || null,
          },
        })
      }
    }
  }, [formMode, editData, getFAAssetDropdown])

  /* -------------------------------------------- */
  /*                SUBMIT HANDLERS               */
  /* -------------------------------------------- */
  const onSubmit = (data: any, approvalStatus: ApprovalStatus) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The change date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }
    }

    if (!selectedAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    if (!selectedCategory) {
      setErrMsg('Please select a new category')
      setErrorDia(true)
      return
    }

    if (
      currentInfo &&
      selectedCategory.fa_category_id === currentInfo.currentCategoryId
    ) {
      setErrMsg('New category must be different from the current category')
      setErrorDia(true)
      return
    }

    const oldCategoryId =
      currentInfo?.currentCategoryId ||
      selectedAsset?.fa_category_id ||
      editData?.old_category_id

    const inputData = {
      category_change_id: editData?.category_change_id || null,
      fa_asset_id: selectedAsset?.fa_asset_id || data.fa_asset_id,
      company_id: CompanyID,
      old_category_id: oldCategoryId,
      new_category_id: selectedCategory?.fa_category_id || data.new_category_id,
      change_date: data.change_date,
      description: data.description,
      reason: data.reason,
      ApprovalStatus: approvalStatus,
    }

    if (mode === 'add' || !editData?.category_change_id) {
      createCategoryChange({
        variables: { input: inputData },
      })
    } else {
      updateCategoryChange({
        variables: { input: inputData },
      })
    }
  }

  const handleAssetChange = (value: any) => {
    if (value) {
      setSelectedAsset(value)
      setValue('fa_asset_id', value?.fa_asset_id)
      setValue('AssetName', value?.name)
      setValue('StartDate', value?.start_use_date)
      setValue('ModelNo', value?.model_no)

      getCurrentCategoryInfo({
        variables: {
          fa_asset_id: value.fa_asset_id,
          exclude_change_id: editData?.category_change_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setCurrentInfo(null)
      setValue('fa_asset_id', '')
      setValue('AssetCategory', 'N/A')
    }
  }

  /* -------------------------------------------- */
  /*               PERIOD VALIDATION              */
  /* -------------------------------------------- */
  const watchChangeDate =
    watch('ChangeDate') || editData?.change_date || new Date()
  const changeDateStr = (() => {
    const d = new Date(watchChangeDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? changeDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        changeDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const changeDate = new Date(watchChangeDate)
  const isDocDateInvalid =
    changeDate.getTime() < minValidDate.getTime() ||
    changeDate.getTime() > maxValidDate.getTime()

  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { disabled: createLoading || updateLoading },
  }

  const rejectFooterOption = {
    name: 'Reject',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Rejected)),
    color: 'secondary',
    props: { disabled: createLoading || updateLoading },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { disabled: createLoading || updateLoading },
  }

  const approveFooterOption = {
    name: 'Approve',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Completed)),
    color: 'primary',
    props: { disabled: createLoading || updateLoading },
  }

  let footerOptions: any[]
  if (editData?.mode === 'resubmit') {
    footerOptions = [submitFooterOption]
  } else if (editData?.mode === 'approve-reject') {
    footerOptions = [rejectFooterOption, approveFooterOption]
  } else {
    footerOptions = [draftFooterOption, submitFooterOption]
  }

  return (
    <>
      {(FACategoryListingLoading ||
        FAAssetLoading ||
        createLoading ||
        updateLoading ||
        currentInfoLoading ||
        openPeriodCheckDateLoading ||
        docDateValidationLoading) && <Loading />}
      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/change-asset-category`
          )
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Change Asset Category', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'approve-reject'
                ? 'Approve/Reject'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: { title: 'Assets Information' },
          }}
        >
          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  options={
                    getFAAssetDropdown?.sort((a, b) => {
                      return a?.DocNo?.localeCompare(b?.DocNo)
                    }) || []
                  }
                  getOptionLabel={option =>
                    `${option?.DocNo || ''} - ${option?.name || ''}`
                  }
                  fullWidth
                  value={selectedAsset}
                  onChange={(event, value: any) => handleAssetChange(value)}
                  disabled={mode === 'approve-reject'}
                  renderOption={option => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{option?.DocNo}</span>
                          <span className="desc"> - {option?.name}</span>
                        </div>
                      </div>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          label="Asset"
                          style={{ width: '100%' }}
                          required
                          margin="normal"
                          helperText={errors?.fa_asset_id?.message}
                          error={errors?.fa_asset_id ? true : false}
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Asset"
            name="fa_asset_id"
            control={control}
            margin="normal"
            ref={register}
            required
            fullWidth
            defaultValue={editData?.fa_asset_id}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="AssetName"
            label="Assets Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            ref={register}
            defaultValue={editData?.Asset?.name ?? 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              as={KeyboardDatePicker}
              name="StartDate"
              label="Start Date"
              control={control}
              format="dd/MM/yyyy"
              readOnly
              margin="dense"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{
                style: { display: 'none' },
                'aria-label': 'change date',
              }}
              className="left"
              defaultValue={editData?.Asset?.start_use_date ?? new Date()}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            id="standard-basic"
            name="ModelNo"
            label="Model No"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            ref={register}
            defaultValue={editData?.Asset?.model_no ?? 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="AssetCategory"
            label="Current Asset Category"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            ref={register}
            defaultValue={
              currentInfo?.currentCategoryName ??
              editData?.OldCategory?.name ??
              'N/A'
            }
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            helperText={
              currentInfo ? `Original: ${currentInfo.originalCategoryName}` : ''
            }
          />
        </CardContents>

        <CardContents
          section={{
            header: { title: 'Asset Category Adjustment' },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              as={KeyboardDatePicker}
              name="change_date"
              label="Change Date"
              control={control}
              format="dd/MM/yyyy"
              margin="dense"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{ 'aria-label': 'change date' }}
              defaultValue={editData?.change_date || new Date()}
              required
              disabled={mode === 'approve-reject'}
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => {
              return (
                <Autocomplete
                  options={
                    getFACategoryDropdownListing?.sort((a, b) => {
                      return a?.name?.localeCompare(b?.name)
                    }) || []
                  }
                  getOptionLabel={option => `${option?.name || ''}`}
                  fullWidth
                  value={selectedCategory}
                  onChange={(event, value: any) => {
                    if (value) {
                      setSelectedCategory(value)
                      setValue('new_category_id', value?.fa_category_id)
                    }
                  }}
                  disabled={mode === 'approve-reject'}
                  renderOption={option => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{option?.name}</span>
                        </div>
                      </div>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          label="New Asset Category"
                          style={{ width: '100%' }}
                          required
                          margin="normal"
                          helperText={errors?.new_category_id?.message}
                          error={errors?.new_category_id ? true : false}
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="New Asset Category"
            name="new_category_id"
            control={control}
            margin="normal"
            ref={register}
            required
            fullWidth
            defaultValue={editData?.new_category_id}
          />

          <VoiceTextField
            mounted={true}
            label="Description"
            name="description"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            setIsFormDirty={setIsFormDirty}
            control={control}
            controllerProps={{
              ref: register,
              autoComplete: 'off',
              defaultValue: editData?.description || '',
            }}
          />

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            setIsFormDirty={setIsFormDirty}
            control={control}
            controllerProps={{
              ref: register,
              autoComplete: 'off',
              defaultValue: editData?.reason || '',
              helperText: errors?.reason?.message,
              error: errors?.reason ? true : false,
            }}
            required
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg="Error"
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/change-asset-category`
          )
        }}
      />
    </>
  )
}
